import { useEffect, useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import {
	ECourseStatus,
	TCourse,
	TCourseCategory,
	TCourseDocument,
	TLesson,
	TLessonDocument,
	TMyClass,
	TSummary,
} from "../../../../@types";
import { apis, callApi } from "../../../../api";
import { AccountService } from "../../../../hooks/useAccount";
import { lessonType } from "../../../../constants";

export type TLocalCourseDocument = {
	type: "local";
	fileType: "image" | "video";
	data: string;
	id: string;
	file: File;
};

export type TLocalLessonDocument = {
	type: "local";
	id: string;
	file: File;
};

export type TMyCourseInstructorService = {
	listeners: (() => void)[];
	loading: boolean;
	isSaving: boolean;
	course: TCourse | null;
	courseDocuments: TCourseDocument[];
	lessons: TLesson[];
	categories: TCourseCategory[];
	summaries: TSummary[];
	openingLessons: Set<number>;

	addEventListener: (cb: () => void) => void;
	setLoading: (loading: boolean) => void;
	updateCourse: (course: Partial<TCourse>) => void;
	setCourse: (course: TCourse) => void;
	setCourseDocuments: (courseDocuments: TCourseDocument[]) => void;
	setLessons: (lessons: TLesson[]) => void;
	setCategories: (categories: TCourseCategory[]) => void;
	setSummaries: (summaries: TSummary[]) => void;
	createLesson: (lesson: TLesson, order: number) => void;
	updateLesson: (lesson: Partial<TLesson> & { id: number }) => void;
	deleteLesson: (lessonId: number) => void;
	toggleOpenLesson: (lessonId: number, open: boolean) => void;
	setIsSaving: (isSaving: boolean) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;
};

export const MyCourseInstructorService: TMyCourseInstructorService = {
	listeners: [],
	loading: false,
	isSaving: false,
	course: null,
	courseDocuments: [],
	lessons: [],
	categories: [],
	summaries: [],
	openingLessons: new Set(),

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	setLoading(loading) {
		this.loading = loading;
	},

	updateCourse(course) {
		if (this.course) {
			this.course = { ...this.course, ...course };
		}
	},

	setCourse(course) {
		this.course = course;
	},

	setCourseDocuments(courseDocuments) {
		this.courseDocuments = courseDocuments;
	},

	setLessons(lessons) {
		this.lessons = lessons;
	},

	setCategories(categories) {
		this.categories = categories;
	},

	setSummaries(summaries) {
		this.summaries = summaries;
	},

	createLesson(lesson, order) {
		this.lessons.splice(order - 1, 0, lesson);
		this.lessons = [...this.lessons];
	},

	updateLesson(lesson) {
		const index = this.lessons.findIndex((l) => l.id === lesson.id);
		if (index === -1) {
			return;
		}

		this.lessons[index] = { ...this.lessons[index], ...lesson };
		this.lessons = [...this.lessons];
	},

	deleteLesson(lessonId) {
		this.lessons = this.lessons.filter((lesson) => lesson.id !== lessonId);
	},

	toggleOpenLesson(lessonId, open) {
		if (open) {
			this.openingLessons.add(lessonId);
		} else {
			this.openingLessons.delete(lessonId);
		}
		this.openingLessons = new Set(this.openingLessons);
	},

	setIsSaving(isSaving) {
		this.isSaving = isSaving;
	},

	clearData() {
		this.course = null;
		this.courseDocuments = [];
		this.lessons = [];
		this.loading = false;
		this.emitChanges();
	},

	/**
	 * Send new course data to all listeners
	 */
	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},
};

/**
 * This hook should call only once
 */
export function useMyCourseInstructorInit() {
	const params = useParams();
	const slug = params.slug ?? "";

	const [isNotFound, setIsNotFound] = useState(false);

	useEffect(() => {
		const getCourseBySlug = async () => {
			try {
				const response = await callApi({
					method: "GET",
					url: `${apis.MY_COURSES_BY_SLUG}/${slug}`,
				});

				if (!response) {
					return;
				}

				MyCourseInstructorService.setCourse(response.course);
				MyCourseInstructorService.setCourseDocuments(response.courseDocuments);
				MyCourseInstructorService.setLessons(response.lessons);
			} catch (error: any) {
				if (error?.response?.status === 404) {
					setIsNotFound(true);
				}
			}
		};

		const getCategories = async () => {
			try {
				const categoryResponse = await callApi({
					method: "GET",
					url: apis.COURSE_CATEGORIES,
				});

				let categories: TCourseCategory[] = [];

				if (categoryResponse?.categories) {
					categories = categoryResponse.categories;
					MyCourseInstructorService.setCategories(categories);
				}
			} catch (error) {}
		};

		const getSummariesBySlug = async () => {
			try {
				const response = await callApi({
					method: "GET",
					url: `${apis.SUMMARY_BY_TEACHER_TOKEN}/${slug}`,
				});

				if (!response) {
					return;
				}

				MyCourseInstructorService.setSummaries(response.summaries);
			} catch (error) {}
		};

		const getData = async () => {
			MyCourseInstructorService.setLoading(true);
			MyCourseInstructorService.emitChanges();
			await Promise.all([
				getCourseBySlug(),
				getCategories(),
				getSummariesBySlug(),
			]);
			MyCourseInstructorService.setLoading(false);
			MyCourseInstructorService.emitChanges();
		};

		getData();
	}, [slug]);

	useEffect(() => {
		return () => {
			MyCourseInstructorService.clearData();
		};
	}, []);

	return { isNotFound, slug };
}

export function useMyCourseInstructor() {
	const [loading, setLoading] = useState(MyCourseInstructorService.loading);
	const [course, setCourse] = useState<TCourse | null>(
		MyCourseInstructorService.course,
	);
	const [courseDocuments, setCourseDocuments] = useState<TCourseDocument[]>(
		MyCourseInstructorService.courseDocuments,
	);
	const [lessons, setLessons] = useState<TLesson[]>(
		MyCourseInstructorService.lessons,
	);
	const [categories, setCategories] = useState<TCourseCategory[]>(
		MyCourseInstructorService.categories,
	);
	const [summaries, setSummaries] = useState<TSummary[]>(
		MyCourseInstructorService.summaries,
	);
	const [openingLessons, setOpeningLessons] = useState<Set<number>>(
		MyCourseInstructorService.openingLessons,
	);
	const [isSaving, setIsSaving] = useState(MyCourseInstructorService.isSaving);

	useEffect(() => {
		const updateData = () => {
			setLoading(MyCourseInstructorService.loading);
			setCourse(MyCourseInstructorService.course);
			setCourseDocuments(MyCourseInstructorService.courseDocuments);
			setLessons(MyCourseInstructorService.lessons);
			setCategories(MyCourseInstructorService.categories);
			setSummaries(MyCourseInstructorService.summaries);
			setOpeningLessons(MyCourseInstructorService.openingLessons);
			setIsSaving(MyCourseInstructorService.isSaving);
		};

		MyCourseInstructorService.addEventListener(updateData);

		return () => {
			MyCourseInstructorService.removeEventListener(updateData);
		};
	}, []);

	const updateCourseBasicInformation = async (
		fieldsToUpdate: Partial<TCourse>,
	) => {
		if (!MyCourseInstructorService.course) {
			return;
		}
		try {
			MyCourseInstructorService.setIsSaving(true);
			MyCourseInstructorService.emitChanges();
			const response = await callApi({
				method: "PATCH",
				url: `${apis.COURSES}/${MyCourseInstructorService.course.id}`,
				body: fieldsToUpdate,
			});

			if (response) {
				MyCourseInstructorService.updateCourse({
					...MyCourseInstructorService.course,
					...response.course,
				});
			}
		} catch (error) {
		} finally {
			MyCourseInstructorService.setIsSaving(false);
			MyCourseInstructorService.emitChanges();
		}
	};

	const uploadCourseDocument = async (file: File) => {
		if (!AccountService.savedAccount || !MyCourseInstructorService.course) {
			return;
		}

		try {
			const getSignedURLResponse = await callApi({
				method: "POST",
				url: apis.GET_SIGNED_URL,
				body: {
					fileName: file.name,
					docType: "courses",
					contentType: file.type,
					userId: AccountService.savedAccount.userId,
				},
			});

			if (!getSignedURLResponse) {
				return;
			}

			const { signedURL, location } = getSignedURLResponse;

			let documentType = "";
			if (file.type.startsWith("image")) {
				documentType = "COURSE_IMG";
			} else if (file.type.startsWith("video")) {
				documentType = "TRAILER";
			}

			await callApi({
				method: "PUT",
				baseURL: "",
				url: signedURL,
				body: file,
			});

			const response = await callApi({
				method: "POST",
				url: apis.COURSE_DOCUMENTS,
				body: {
					courseId: MyCourseInstructorService.course.id,
					fileName: file.name,
					contentType: file.type,
					location,
					documentType,
				},
			});

			if (response?.doc) {
				return response.doc;
			}

			return null;
		} catch (error) {
			return null;
		}
	};

	const removeCourseDocument = async (id: number) => {
		try {
			await callApi({
				method: "DELETE",
				url: `${apis.COURSE_DOCUMENTS}/${id}`,
			});
		} catch (error) {}
	};

	const updateCourseDocuments = async (
		mediaFiles: (TCourseDocument | TLocalCourseDocument)[],
	) => {
		try {
			if (!MyCourseInstructorService.course) {
				return;
			}

			MyCourseInstructorService.setIsSaving(true);
			MyCourseInstructorService.emitChanges();

			const newMediaFiles = [...mediaFiles];
			const currentMediaFiles = MyCourseInstructorService.courseDocuments;
			const ids = mediaFiles.map((mediaFile) => mediaFile.id);

			const removePromises: Promise<void>[] = [];

			for (let i = 0; i < currentMediaFiles.length; i++) {
				if (!ids.includes(currentMediaFiles[i].id)) {
					const promise = removeCourseDocument(currentMediaFiles[i].id);
					removePromises.push(promise);
				}
			}

			await Promise.all(removePromises);

			const uploadPromises: Promise<any>[] = [];
			const uploadIndexes: number[] = [];

			for (let i = 0; i < mediaFiles.length; i++) {
				const mediaFile = mediaFiles[i];
				if (mediaFile.type === "local") {
					const promise = uploadCourseDocument(mediaFile.file);
					uploadPromises.push(promise);
					uploadIndexes.push(i);
				}
			}

			const uploadedFiles = await Promise.all(uploadPromises);
			for (let i = 0; i < uploadedFiles.length; i++) {
				if (uploadedFiles[i]) {
					newMediaFiles[uploadIndexes[i]] = uploadedFiles[i];
				}
			}

			await callApi({
				method: "POST",
				url: apis.COURSE_REORDER_DOCUMENTS,
				body: {
					courseId: MyCourseInstructorService.course.id,
					newDocumentsOrder: newMediaFiles.map((file) => file.id),
				},
			});

			MyCourseInstructorService.setCourseDocuments(
				newMediaFiles as TCourseDocument[],
			);
		} catch (error) {
			MyCourseInstructorService.setCourseDocuments(
				MyCourseInstructorService.courseDocuments,
			);
		} finally {
			MyCourseInstructorService.setIsSaving(false);
			MyCourseInstructorService.emitChanges();
		}
	};

	const updateCourseStatus = async (newStatus: ECourseStatus) => {
		try {
			if (!MyCourseInstructorService.course) {
				return;
			}

			MyCourseInstructorService.setLoading(true);
			MyCourseInstructorService.emitChanges();

			await callApi({
				method: "PATCH",
				url: `${apis.COURSE_STATUS}/${MyCourseInstructorService.course.id}`,
				body: {
					status: newStatus,
				},
			});

			MyCourseInstructorService.updateCourse({ status: newStatus });
		} catch (error) {
		} finally {
			MyCourseInstructorService.setLoading(false);
			MyCourseInstructorService.emitChanges();
		}
	};

	const createLesson = async (name: string, order: number) => {
		try {
			if (!MyCourseInstructorService.course) {
				return;
			}
			const isLast = order === MyCourseInstructorService.lessons.length + 1;
			const response = await callApi({
				method: "POST",
				url: apis.LESSONS,
				body: {
					courseId: MyCourseInstructorService.course.id,
					title: name,
					type: lessonType.ONSITE,
				},
			});

			if (!response?.lesson) {
				return;
			}
			MyCourseInstructorService.createLesson(
				{ ...response.lesson, lessonDocuments: [] },
				order,
			);
			MyCourseInstructorService.toggleOpenLesson(response.lesson.id, true);

			if (!isLast) {
				await callApi({
					method: "POST",
					url: apis.LESSONS_REORDER,
					body: {
						courseId: MyCourseInstructorService.course.id,
						newDocumentsOrder: MyCourseInstructorService.lessons.map(
							(l) => l.id,
						),
					},
				});
			}

			MyCourseInstructorService.emitChanges();
		} catch (error) {}
	};

	const updateLesson = async (data: Partial<TLesson>, lessonId: number) => {
		try {
			MyCourseInstructorService.setIsSaving(true);
			MyCourseInstructorService.emitChanges();
			const response = await callApi({
				method: "PATCH",
				url: `${apis.LESSONS}/${lessonId}`,
				body: data,
			});
			if (!response?.lesson) {
				return;
			}
			MyCourseInstructorService.updateLesson(response.lesson);
		} catch (error) {
		} finally {
			MyCourseInstructorService.setIsSaving(false);
			MyCourseInstructorService.emitChanges();
		}
	};

	const updateLessonDocument = async (
		lessonDocuments: (TLessonDocument | TLocalLessonDocument)[],
		lessonId: number,
	) => {
		try {
			const lesson = MyCourseInstructorService.lessons.find(
				(l) => l.id === lessonId,
			);
			if (!lesson) {
				return;
			}

			MyCourseInstructorService.setIsSaving(true);
			MyCourseInstructorService.emitChanges();

			const newLessonDocuments = [...lessonDocuments];

			const ids = lessonDocuments.map((doc) => doc.id);

			const removePromises: Promise<void>[] = [];

			for (let i = 0; i < lesson.lessonDocuments.length; i++) {
				if (!ids.includes(lesson.lessonDocuments[i].id)) {
					const promise = removeLessonDocument(
						lessonId,
						lesson.lessonDocuments[i].id,
					);
					removePromises.push(promise);
				}
			}

			await Promise.all(removePromises);

			const uploadPromises: Promise<any>[] = [];
			const uploadIndexes: number[] = [];

			for (let i = 0; i < lessonDocuments.length; i++) {
				const lessonDocument = lessonDocuments[i];
				if (lessonDocument.type === "local") {
					const promise = uploadLessonDocument(lessonId, lessonDocument.file);
					uploadPromises.push(promise);
					uploadIndexes.push(i);
				}
			}

			const uploadedFiles = await Promise.all(uploadPromises);
			for (let i = 0; i < uploadedFiles.length; i++) {
				if (uploadedFiles[i]) {
					newLessonDocuments[uploadIndexes[i]] = uploadedFiles[i];
				}
			}

			MyCourseInstructorService.updateLesson({
				lessonDocuments: newLessonDocuments as TLessonDocument[],
				id: lessonId,
			});
		} catch (error) {
		} finally {
			MyCourseInstructorService.setIsSaving(false);
			MyCourseInstructorService.emitChanges();
		}
	};

	const uploadLessonDocument = async (lessonId: number, file: File) => {
		if (!AccountService.savedAccount || !MyCourseInstructorService.course) {
			return;
		}

		try {
			const getSignedURLResponse = await callApi({
				method: "POST",
				url: apis.GET_SIGNED_URL,
				body: {
					fileName: file.name,
					docType: "lessons",
					contentType: file.type,
					userId: AccountService.savedAccount.userId,
				},
			});

			if (!getSignedURLResponse) {
				return;
			}

			const { signedURL, location } = getSignedURLResponse;

			await callApi({
				method: "PUT",
				baseURL: "",
				url: signedURL,
				body: file,
			});

			const response = await callApi({
				method: "POST",
				url: apis.LESSONS_DOCUMENTS,
				urlParams: {
					lessonId: `${lessonId}`,
				},
				body: {
					fileName: file.name,
					contentType: file.type,
					location,
					documentType: "HELP_DOC",
				},
			});

			if (response?.doc) {
				return response.doc;
			}

			return null;
		} catch (error) {
			return null;
		}
	};

	const removeLessonDocument = async (lessonId: number, documentId: number) => {
		try {
			await callApi({
				method: "DELETE",
				url: `${apis.LESSONS_DOCUMENTS}/${documentId}`,
				urlParams: { lessonId: `${lessonId}` },
			});
		} catch (error) {}
	};

	const reorderLessons = async (newLessons: TLesson[]) => {
		try {
			if (!MyCourseInstructorService.course) {
				return;
			}

			MyCourseInstructorService.setLessons(newLessons);
			MyCourseInstructorService.emitChanges();

			await callApi({
				method: "POST",
				url: apis.LESSONS_REORDER,
				body: {
					courseId: MyCourseInstructorService.course.id,
					newDocumentsOrder: MyCourseInstructorService.lessons.map((l) => l.id),
				},
			});
		} catch (error) {}
	};

	const deleteLesson = async (lessonId: number) => {
		try {
			callApi({
				method: "DELETE",
				url: `${apis.LESSONS}/${lessonId}`,
			});
			MyCourseInstructorService.deleteLesson(lessonId);
			MyCourseInstructorService.emitChanges();
		} catch (error) {}
	};

	const toggleOpenLesson = (lessonId: number, open: boolean) => {
		MyCourseInstructorService.toggleOpenLesson(lessonId, open);
		MyCourseInstructorService.emitChanges();
	};

	const { learningSummaries, upcomingSummaries, finishedSummaries } =
		useMemo(() => {
			return splitSummaries(summaries);
		}, [summaries]);

	return {
		isSaving,
		loading,
		course,
		courseDocuments,
		lessons,
		categories,
		summaries,
		learningSummaries,
		upcomingSummaries,
		finishedSummaries,
		openingLessons,
		updateCourseBasicInformation,
		updateCourseDocuments,
		updateCourseStatus,
		createLesson,
		updateLesson,
		updateLessonDocument,
		reorderLessons,
		deleteLesson,
		toggleOpenLesson,
	};
}

export type TProgressSummary = TSummary & {
	nextAppointment?: TMyClass;
	lastAppointment?: TMyClass;
	passedCount: number;
};

const splitSummaries = (summaries: TSummary[]) => {
	const learningSummaries: TProgressSummary[] = [];
	const finishedSummaries: TProgressSummary[] = [];
	const upcomingSummaries: TProgressSummary[] = [];

	for (let i = 0; i < summaries.length; i++) {
		const summary = summaries[i];
		let passedCount = 0;
		let nextAppointment: TMyClass | undefined = undefined;
		let lastAppointment: TMyClass | undefined = undefined;
		let airHours = 0;
		let simulatorHours = 0;
		let milesFlown = 0;
		let flightSummary =
			"Students love feedback, tips, and personal assignments. What can you pass on to the student to accelerate learning?";
		for (let j = 0; j < summary.bookings.length; j++) {
			const booking = summary.bookings[j];
			if (booking.flightSummary) {
				flightSummary = booking.flightSummary;
			}
			airHours += booking.airHours;
			simulatorHours += booking.simulatorHours;
			milesFlown += booking.milesFlown;
			if (
				booking.lesson.type === lessonType.ONSITE &&
				new Date() > new Date(booking.endTime ?? "")
			) {
				passedCount++;
				lastAppointment = booking;
				continue;
			}
			if (booking.lesson.type === lessonType.ONLINE && booking.passed) {
				passedCount++;
				continue;
			}

			if (booking.lesson.type === lessonType.ONSITE && !nextAppointment) {
				nextAppointment = booking;
			}
		}

		if (passedCount === summary.bookings.length) {
			finishedSummaries.push({
				...summary,
				lastAppointment,
				airHours,
				simulatorHours,
				milesFlown,
				flightSummary,
				passedCount,
			});
		} else if (passedCount === 0) {
			upcomingSummaries.push({ ...summary, nextAppointment, passedCount });
		} else if (passedCount < summary.bookings.length) {
			learningSummaries.push({
				...summary,
				lastAppointment,
				nextAppointment,
				airHours,
				simulatorHours,
				milesFlown,
				flightSummary,
				passedCount,
			});
		}
	}

	learningSummaries.sort((a, b) => {
		if (a.lastAppointment && b.lastAppointment) {
			return (
				new Date(b.lastAppointment.startTime ?? "").getTime() -
				new Date(a.lastAppointment.startTime ?? "").getTime()
			);
		}

		// Online course
		if (!a.lastAppointment && !b.lastAppointment) {
			return 0;
		}

		if (!a.lastAppointment) {
			return 1;
		}

		if (!b.lastAppointment) {
			return -1;
		}

		return 0;
	});

	upcomingSummaries.sort((a, b) => {
		if (a.nextAppointment && b.nextAppointment) {
			return (
				new Date(a.nextAppointment.startTime ?? "").getTime() -
				new Date(b.nextAppointment.startTime ?? "").getTime()
			);
		}

		// Online course
		if (!a.nextAppointment && !b.nextAppointment) {
			return 0;
		}

		if (!a.nextAppointment) {
			return 1;
		}

		if (!b.nextAppointment) {
			return -1;
		}

		return 0;
	});

	finishedSummaries.sort((a, b) => {
		if (a.lastAppointment && b.lastAppointment) {
			return (
				new Date(b.lastAppointment.startTime ?? "").getTime() -
				new Date(a.lastAppointment.startTime ?? "").getTime()
			);
		}

		// Online course
		if (!a.lastAppointment && !b.lastAppointment) {
			return 0;
		}

		if (!a.lastAppointment) {
			return 1;
		}

		if (!b.lastAppointment) {
			return -1;
		}

		return 0;
	});

	return { learningSummaries, upcomingSummaries, finishedSummaries };
};
